<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <router-link :to="`/app/exams/${examId}/subjects`" class="btn btn-outline-secondary btn-sm">
          <vue-feather type="arrow-left" size="14" class="me-1" />
          Back
        </router-link>
      </template>
    </breadcrumb-index>

    <div class="card">
      <div class="card-header">
        <h6 class="card-title mb-0">Edit Subject</h6>
      </div>
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <div class="row">
            <div class="col-lg-8">
              <div class="row">
                <div class="col-md-8 mb-3">
                  <label class="form-label">Subject Name <span class="text-danger">*</span></label>
                  <input v-model="form.name" type="text" class="form-control" required />
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-label">Display Order</label>
                  <input v-model.number="form.order_index" type="number" class="form-control" min="0" />
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Description</label>
                <textarea v-model="form.description" class="form-control" rows="3"></textarea>
              </div>

              <div class="row">
                <div class="col-md-4 mb-3">
                  <label class="form-label">Active</label>
                  <select v-model="form.is_active" class="form-select">
                    <option :value="true">Active</option>
                    <option :value="false">Inactive</option>
                  </select>
                </div>
              </div>

              <div v-if="error" class="alert alert-danger">{{ error }}</div>

              <div class="d-flex gap-2 pt-2">
                <button type="submit" class="btn btn-primary" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
                  Update Subject
                </button>
                <router-link :to="`/app/subjects/${subjectId}/topics`" class="btn btn-outline-info">
                  <vue-feather type="list" size="14" class="me-1" />
                  Manage Topics
                </router-link>
                <router-link :to="`/app/exams/${examId}/subjects`" class="btn btn-outline-secondary">Cancel</router-link>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'

export default {
  name: 'EditSubject',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const alert = useAlert()
    const subjectId = route.params.id

    const subject = ref(null)
    const examId = ref(null)
    const form = ref({
      name: '',
      description: '',
      order_index: 1,
      is_active: true
    })
    const loading = ref(false)
    const error = ref('')

    const breadcrumbs = computed(() => {
      const items = [
        { title: 'Dashboard', url: '/app/dashboard' },
        { title: 'Exams', url: '/app/exams/list' }
      ]
      // Exam → Subject (edit)
      if (subject.value?.examId) {
        items.push({ title: subject.value?.examName || 'Exam', url: `/app/exams/${subject.value.examId}/subjects` })
      }
      items.push({ title: subject.value?.name || 'Edit Subject', active: true })
      return items
    })

    const fetchSubject = async () => {
      try {
        const response = await api.get(`/v1/admin/subjects/${subjectId}`)
        subject.value = response.data.subject
        examId.value = response.data.subject.examId
        form.value = {
          name: response.data.subject.name,
          description: response.data.subject.description || '',
          order_index: response.data.subject.orderIndex ?? response.data.subject.order_index ?? 1,
          is_active: response.data.subject.isActive ?? response.data.subject.is_active ?? true
        }
      } catch (err) {
        alert.error('Error', 'Subject not found')
        router.push('/app/exams/list')
      }
    }

    const handleSubmit = async () => {
      loading.value = true
      error.value = ''

      try {
        await api.put(`/v1/admin/subjects/${subjectId}`, form.value)
        alert.success('Success', 'Subject updated successfully')
        router.push(`/app/exams/${examId.value}/subjects`)
      } catch (err) {
        error.value = err.response?.data?.message || 'Failed to update subject'
      } finally {
        loading.value = false
      }
    }

    onMounted(fetchSubject)

    return { subjectId, examId, form, loading, error, breadcrumbs, handleSubmit }
  }
}
</script>
